import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../../config";
import "./AddDevice.css";

const AddDeviceForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [popList, setPopList] = useState([]);
  const [credList, setCredList] = useState([]);
  const [profileList, setProfileList] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [modelList, setModelList] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [deviceData, setDeviceData] = useState({
    device_type: "",
    technology: "",
    vendor_profile_id: "",
    vendor_name: "",
    model_name: "",
    hostname: "",
    ip_address: "",
    serial_no: "",
    mac_address: "",
    pop_id: "",
    credential_id: ""
  });

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [popsRes, credsRes, profilesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/info-manage/pops`),
          fetch(`${API_BASE_URL}/info-manage/credentials`),
          fetch(`${API_BASE_URL}/device-manage/vendors`)
        ]);
        const pops = await popsRes.json();
        const creds = await credsRes.json();
        const profiles = await profilesRes.json();
        if (pops.success) setPopList(pops.data);
        if (creds.success) setCredList(creds.data);
        if (profiles.success) setProfileList(profiles.data);
      } catch (err) { console.error(err); }
    };
    fetchAll();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "device_type") {
      const vendors = [...new Set(
        profileList
          .filter(p => p.device_type === value)
          .map(p => p.vendor_name)
      )];
      setVendorList(vendors);
      setModelList([]);
      setSelectedProfile(null);
      setDeviceData(prev => ({ ...prev, device_type: value, technology: "", vendor_name: "", model_name: "", vendor_profile_id: "" }));
      return;
    }

    if (name === "vendor_name") {
      const models = profileList.filter(
        p => p.device_type === deviceData.device_type && p.vendor_name === value
      );
      setModelList(models);
      setSelectedProfile(null);
      setDeviceData(prev => ({ ...prev, vendor_name: value, model_name: "", vendor_profile_id: "" }));
      return;
    }

    if (name === "vendor_profile_id") {
      const profile = profileList.find(p => p.id === parseInt(value));
      setSelectedProfile(profile || null);
      setDeviceData(prev => ({ ...prev, vendor_profile_id: value, model_name: profile?.model_name || "" }));
      return;
    }

    setDeviceData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/device-manage/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(deviceData)
      });
      const result = await res.json();
      if (result.success) {
        setShowModal(true);
      } else {
        alert(result.error || "Something went wrong");
      }
    } catch (err) {
      alert("Error connecting to server");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ad-container">
      <div className="ad-card">
        <h2 className="ad-title">{t("add_device_info")}</h2>
        <form className="ad-form" onSubmit={handleSubmit}>

          <div className="ad-grid">
            <div className="ad-input-group">
              <label>Device Type</label>
              <select name="device_type" value={deviceData.device_type} onChange={handleChange} required>
                <option value="">Select Type</option>
                <option value="OLT">OLT</option>
                <option value="Router">Router</option>
                <option value="Switch">Switch</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {deviceData.device_type === "OLT" && (
              <div className="ad-input-group">
                <label>Technology</label>
                <select name="technology" value={deviceData.technology} onChange={handleChange} required>
                  <option value="">Select Technology</option>
                  <option value="EPON">EPON</option>
                  <option value="GPON">GPON</option>
                </select>
              </div>
            )}
          </div>

          <div className="ad-grid">
            <div className="ad-input-group">
              <label>Vendor</label>
              <select name="vendor_name" value={deviceData.vendor_name} onChange={handleChange} required disabled={!deviceData.device_type}>
                <option value="">Select Vendor</option>
                {vendorList.map((v, i) => (
                  <option key={i} value={v}>{v}</option>
                ))}
              </select>
            </div>

            <div className="ad-input-group">
              <label>Model</label>
              <select name="vendor_profile_id" value={deviceData.vendor_profile_id} onChange={handleChange} required disabled={!deviceData.vendor_name}>
                <option value="">Select Model</option>
                {modelList.map(m => (
                  <option key={m.id} value={m.id}>{m.model_name}</option>
                ))}
              </select>
            </div>
          </div>

          {selectedProfile && (
            <div className="ad-info-badge">
              Driver: <b>{selectedProfile.driver_file}</b> &nbsp;|&nbsp;
              Log Method: <b>{selectedProfile.log_method}</b> &nbsp;|&nbsp;
              Access: <b>{selectedProfile.access_method}</b>
            </div>
          )}

          <div className="ad-grid">
            <div className="ad-input-group">
              <label>Hostname</label>
              <input type="text" name="hostname" value={deviceData.hostname} onChange={handleChange} required placeholder="Ex: gb_1_olt1" />
            </div>
            <div className="ad-input-group">
              <label>IP Address</label>
              <input type="text" name="ip_address" value={deviceData.ip_address} onChange={handleChange} required placeholder="Ex: 10.100.93.1" />
            </div>
          </div>

          <div className="ad-grid">
            <div className="ad-input-group">
              <label>Serial No</label>
              <input type="text" name="serial_no" value={deviceData.serial_no} onChange={handleChange} placeholder="Serial number" />
            </div>
            <div className="ad-input-group">
              <label>MAC Address</label>
              <input type="text" name="mac_address" value={deviceData.mac_address} onChange={handleChange} placeholder="Ex: E0:67:B3:7A:0E:00" />
            </div>
          </div>

          <div className="ad-grid">
            <div className="ad-input-group">
              <label>POP / Location</label>
              <select name="pop_id" value={deviceData.pop_id} onChange={handleChange}>
                <option value="">Select POP</option>
                {popList.map(p => (
                  <option key={p.id} value={p.id}>{p.pop_name}</option>
                ))}
              </select>
            </div>
            <div className="ad-input-group">
              <label>Credential</label>
              <select name="credential_id" value={deviceData.credential_id} onChange={handleChange}>
                <option value="">Select Credential</option>
                {credList.map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          <button type="button" className="ad-submit-btn" style={{background:"#475569", marginTop:"0"}} onClick={() => navigate(-1)}>Back</button>
          <button className="ad-submit-btn" type="submit" disabled={loading}>
            {loading ? "Saving..." : t("add_device")}
          </button>
        </form>
      </div>

      {showModal && (
        <div className="pop-modal-overlay">
          <div className="pop-modal-box">
            <h3>✅ Successful!</h3>
            <p>Device added successfully.</p>
            <button onClick={() => navigate("/device-manage/device-list")} className="ad-submit-btn" style={{ width: "100%", marginTop: "10px" }}>
              Go to Device List
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddDeviceForm;
